import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'

export interface RemoveFromWorkspaceDialogProps {
  showing: boolean
  onClose: () => void
  /**
   * The single user being removed, or `null` when removing a selection.
   */
  selectedUserName: string | null
  selectedUserCount: number
  workspaceName: string
  onSubmit: () => void
  isSubmitPending: boolean
  /**
   * The confirmation input.
   */
  children: ReactNode
}

export function RemoveFromWorkspaceDialog({
  showing,
  onClose,
  selectedUserName,
  selectedUserCount,
  workspaceName,
  onSubmit,
  isSubmitPending,
  children,
}: RemoveFromWorkspaceDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            Remove user{selectedUserCount > 1 ? <>s</> : null} from this
            workspace
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            This action will remove{' '}
            {selectedUserName !== null ? (
              <b> {selectedUserName} </b>
            ) : (
              <b> {selectedUserCount} users </b>
            )}{' '}
            from the <b>{workspaceName}</b> workspace. Are you sure you want to
            proceed?
          </ModalSheetContentDescription>
          <ModalSheetSection>{children}</ModalSheetSection>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
          />
          <Button
            label={
              selectedUserName !== null
                ? 'Remove user'
                : `Remove ${selectedUserCount} users`
            }
            variant="destructive"
            testId="user-management-remove-from-workspace-remove-button"
            onPress={onSubmit}
            isDisabled={isSubmitPending}
            isPending={isSubmitPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
